// src/components/Auth.jsx  (AUTH V3) — FIXED 2FA (no bypass) — UI unchanged
import React, { useEffect, useState } from "react";
import "./auth.css";
import Footer from "./Footer";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";

import { TOTP, Secret } from "otpauth"; // <-- for 2FA

function passwordStrength(password) {
  let score = 0;
  if (!password) return 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
}

export default function AuthV3() {
  const navigate = useNavigate();

  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [pwdScore, setPwdScore] = useState(0);

  // 2FA States (fixed logic)
  const [require2FA, setRequire2FA] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState("");
  const [code, setCode] = useState("");
  const [twoMsg, setTwoMsg] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);

  // store credentials temporarily when 2FA required
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    setPwdScore(passwordStrength(pass));
  }, [pass]);

  // Auto redirect if logged in
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) navigate("/", { replace: true });
    });
  }, [navigate]);

  // ---------------- GOOGLE LOGIN ----------------
  const handleGoogle = async () => {
    setError("");
    setInfo("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: "https://mintiq.pages.dev" },
    });
    if (error) setError(error.message);
  };

  // ---------------- EMAIL LOGIN (WITH 2FA CHECK) ----------------
  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");
    setTwoMsg("");

    try {
      // 1) attempt password sign-in
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password: pass,
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      const user = data.user;

      // 2) fetch user's settings (to check 2FA)
      const { data: row, error: rowErr } = await supabase
        .from("users_data")
        .select("settings")
        .eq("user_id", user.id)
        .single();

      if (rowErr) {
        // if settings row not found treat as no-2fa
        setLoading(false);
        navigate("/");
        return;
      }

      const settings = row?.settings || {};

      // 3) if 2FA enabled => force OTP flow (prevent bypass)
      if (settings.authEnabled && settings.authSecret) {
        // Immediately sign out server session to avoid bypass
        await supabase.auth.signOut();

        // Save creds locally for re-login after OTP verification
        setPendingUser({ email, password: pass });

        // Open 2FA popup and set the secret for verification
        setTwoFASecret(settings.authSecret);
        setRequire2FA(true);
        setLoading(false);
        return;
      }

      // 4) no 2FA -> success, continue
      setLoading(false);
      navigate("/");
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed");
      setLoading(false);
    }
  };

  // ---------------- VERIFY 2FA ----------------
  const verify2FA = async () => {
    setVerifyLoading(true);
    setTwoMsg("");
    setError("");
    setInfo("");

    try {
      if (!pendingUser) {
        setTwoMsg("Missing credentials — please login first.");
        setVerifyLoading(false);
        return;
      }

      const totp = new TOTP({
        issuer: "MyApp Secure",
        label: pendingUser.email,
        secret: Secret.fromBase32(twoFASecret),
      });

      const delta = totp.validate({ token: code });

      if (delta === null) {
        setTwoMsg("Invalid code");
        setVerifyLoading(false);
        return;
      }

      // OTP correct → re-login with stored credentials
      const { data, error } = await supabase.auth.signInWithPassword({
        email: pendingUser.email,
        password: pendingUser.password,
      });

      if (error) {
        setTwoMsg("Login after verification failed");
        setVerifyLoading(false);
        return;
      }

      // success -> clear pending data & go to dashboard
      setPendingUser(null);
      setRequire2FA(false);
      setTwoFASecret("");
      setCode("");
      setTwoMsg("");
      setVerifyLoading(false);
      navigate("/");
    } catch (err) {
      console.error("2FA verify error:", err);
      setTwoMsg("Verification failed");
      setVerifyLoading(false);
    }
  };

  // ---------------- SIGNUP ----------------
  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (pass !== confirmPass) {
      setError("Passwords do not match.");
      return;
    }
    if (pwdScore < 2) {
      setError("Password too weak.");
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password: pass,
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      if (data?.user?.id) {
        await supabase.from("user_stats").insert({
          user_id: data.user.id,
          balance: 0,
          total_invested: 0,
          todays_profit: 0,
          total_payout: 0,
          pending_profits: 0,
          withdraw_limit_today: 0,
          reward_balance: 0,
        });
      }

      setInfo("Account created. Please login.");
    } catch (err) {
      console.error("Signup error:", err);
      setError("Signup failed");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- RESET PASSWORD ----------------
  const handleReset = async () => {
    setError("");
    setInfo("");

    const { error } = await supabase.auth.resetPasswordForEmail(email);

    if (error) setError(error.message);
    else setInfo("Password reset email sent.");
  };

  const pwdLabel = ["Very weak", "Weak", "Okay", "Strong", "Very strong"];

  return (
    <div className="auth-page">

      {/* HEADER */}
      <header className="auth-header">
        <div className="auth-left">
          <img src="/logo.svg" className="qt-logo" alt="logo" />
          <div className="logo-text">
            <div className="qt-title">MintIQ</div>
            <div className="qt-sub">WEB Puzzles PLATFORM</div>
          </div>
        </div>

        <div className="auth-right">
          <button className={`auth-btn ${isLogin ? "active-dark" : ""}`} onClick={() => setIsLogin(true)}>Login</button>
          <button className={`auth-btn-green ${!isLogin ? "active-green" : ""}`} onClick={() => setIsLogin(false)}>Signup</button>
        </div>
      </header>

      <div className="qx-body-bg" style={{ backgroundImage: "url('/auth-bg.svg')" }} />

      <div className="auth-center-box">
        <div className="qx-card">
          <h2 className="qx-title">{isLogin ? "Sign In" : "Create Account"}</h2>

          {error && <div className="auth-error">{error}</div>}
          {info && <div className="auth-info">{info}</div>}

          <form onSubmit={isLogin ? handleLogin : handleSignup}>
            <input
              className="qx-input"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <div style={{ position: "relative", marginTop: 12 }}>
              <input
                className="qx-input"
                type={showPass ? "text" : "password"}
                placeholder="Password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                className="show-pass-btn"
              >
                {showPass ? "Hide" : "Show"}
              </button>
            </div>

            {!isLogin && (
              <input
                className="qx-input"
                type={showPass ? "text" : "password"}
                placeholder="Confirm password"
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                required
                style={{ marginTop: 12 }}
              />
            )}

            <div className="pwd-meter">
              <div className={`bar bar-${pwdScore}`} />
              <div className="pwd-text">{pwdLabel[pwdScore]}</div>
            </div>

            <div className="form-row">
              <button type="button" className="link-like" onClick={handleReset}>
                Forgot password?
              </button>
            </div>

            <button className="qx-btn" type="submit" disabled={loading}>
              {loading ? <span className="spinner" /> : isLogin ? "Login" : "Signup"}
            </button>
          </form>

          <div className="or"></div>
        </div>
      </div>

      <Footer />

      {/* =============================
            2FA POPUP OVERLAY
      ============================= */}
      {require2FA && (
        <div className="qx-2fa-overlay">
          <div className="qx-2fa-box">
            <h3 className="qx-2fa-title">Enter 2FA Code</h3>
            <p className="qx-2fa-text">
              Open Google Authenticator and enter the 6-digit code.
            </p>

            <input
              type="text"
              maxLength={6}
              className="qx-2fa-input"
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />

            <button
              className="qx-btn qx-2fa-btn"
              onClick={verify2FA}
              disabled={verifyLoading}
            >
              {verifyLoading ? <div className="qx-spinner"></div> : "Verify Login"}
            </button>

            {twoMsg && <p className="auth-error">{twoMsg}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
